/**
 * 引导对话（原站 rt 组件 / GaXiAo）。
 *
 * 三种形态（由数据决定，逐字还原自 bundle）：
 *  - type === 1：content 是「页数组」，options 是与之平行的「二维数组」
 *                显示一页 -> 打字完 600ms 后隐藏按钮 -> 再过 300ms 翻到下一页
 *  - type === 2：contents 以 due 指向题目的作答为键（t/f）；若该题尚未作答，
 *                原站会渲染空文本 + 空按钮（死胡同），这里降级为「点击任意处继续」
 *  - 其他（含未定义 type）：单段 content + 一维 options
 *
 * 另外两处原站细节：
 *  - 所有引导文本都会做 {nickname} 替换：nickName 存在则取 '#' 前一段，否则用"博士"
 *  - 当 options 为空时，点击窗口任意位置即视为完成该段引导
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccount } from '../Account/AccountContext';
import type { AnswerKey, Guide, Selection } from '../../types';
import { AnswerButton } from './AnswerButton';
import { Typewriter } from './Typewriter';
import './dialogue.css';

/** 点击选项后的完成延迟（原站 setTimeout(...,600)） */
const SUBMIT_DELAY = 600;
/** type 1 隐藏按钮到翻页之间的间隔（原站 300） */
const PAGE_GAP = 300;

export interface GuidePanelProps {
  guide: Guide;
  gId: string;
  selection: Selection;
  onSubmit: (gId: string) => void;
}

/** 把 string[] 或 string[][] 统一成二维（每页一组选项） */
function toPages(options: Guide['options']): string[][] {
  if (!options?.length) return [];
  return options.map((row) => (Array.isArray(row) ? row : [row]));
}

export function GuidePanel({ guide, gId, selection, onSubmit }: GuidePanelProps) {
  const { nickname } = useAccount();
  const [page, setPage] = useState(0);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [pending, setPending] = useState(false);
  const timer = useRef<number | null>(null);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  useEffect(() => {
    setPage(0);
    setButtonsVisible(false);
    setPending(false);
    clearTimers();
    return clearTimers;
  }, [gId]);

  /** {nickname} 替换（原站对整段文本做全局替换） */
  const fill = useCallback((text: string) => text.replace(/\{nickname\}/g, nickname), [nickname]);

  const isMultiPage = guide.type === 1 && Array.isArray(guide.content);

  /** 当前页文本 */
  const text = useMemo(() => {
    if (guide.type === 2 && guide.contents) {
      const due = guide.due ? selection[guide.due] : undefined;
      if (!due) return '';
      return fill(guide.contents[due as AnswerKey] ?? '');
    }
    if (Array.isArray(guide.content)) return fill(guide.content[page] ?? '');
    return fill(guide.content ?? '');
  }, [guide, page, selection, fill]);

  const pages = useMemo(() => toPages(guide.options), [guide.options]);
  const row = pages[Math.min(page, Math.max(0, pages.length - 1))] ?? [];
  const optionCount = guide.options?.length ?? 0;

  /** 完成本段引导（带 600ms 延迟，与原站一致） */
  const complete = useCallback(() => {
    setPending(true);
    timer.current = window.setTimeout(() => onSubmit(gId), SUBMIT_DELAY);
  }, [gId, onSubmit]);

  /** 点击选项：延迟 600ms 后完成 */
  const choose = useCallback(() => {
    if (pending) return;
    complete();
  }, [pending, complete]);

  /** 打字结束的后续动作（原站 onEnd） */
  const handleTypeEnd = useCallback(() => {
    setButtonsVisible(true);
    if (isMultiPage) {
      const total = (guide.content as string[]).length;
      // 打完 -> 600ms 后隐藏按钮 -> 300ms 后翻页（末页则交给用户点击）
      const t1 = window.setTimeout(() => {
        setButtonsVisible(false);
        if (page < total - 1) {
          const t2 = window.setTimeout(() => {
            setPage((p) => p + 1);
            setButtonsVisible(false);
          }, PAGE_GAP);
          timers.current.push(t2);
        } else {
          setButtonsVisible(true);
        }
      }, SUBMIT_DELAY);
      timers.current.push(t1);
    } else if (optionCount === 0) {
      // 没有任何选项：点击窗口任意位置即完成
      const handler = () => {
        window.removeEventListener('click', handler);
        complete();
      };
      window.addEventListener('click', handler);
      timers.current.push(
        window.setTimeout(() => window.removeEventListener('click', handler), 60_000),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiPage, page, optionCount, guide.content, complete]);

  return (
    <div className="guide">
      <Typewriter text={text} onEnd={handleTypeEnd} />
      {optionCount > 0 && !(isMultiPage && !buttonsVisible) && (
        <div className="answerGroup" data-phase="entered">
          {row.map((label) => (
            <div className="answerWrapper" data-phase="entered" key={label}>
              <AnswerButton text={label} disabled={pending} onClick={choose} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
